import { EVENTS_TYPES_CONTENT, REPLACE_KEYS } from 'constants/history-event-type.constants';
import { IRelevantInfo, IShipmentDestinationInfo } from 'types/suscription/history.types';
import { ILogisticServiceHistoryEvent } from 'types/suscription/history.types';
import { IOrderHistoryEvent } from 'types/suscription/history.types';

/**
 * Reemplaza el placeholder de destino en un texto usando destinationPlace, receiverName y destinationCountryName
 * @param destination - Información del destino
 * @returns Texto con el placeholder de destino reemplazado
 */
export const replaceDestinationPlaceholder = (destination: IShipmentDestinationInfo) => {
  const { destinationPlace, receiverName, destinationCountryName } = destination;
  if (destinationPlace === 'user') return receiverName;
  if (destinationPlace === 'office') return `Oficinas ${receiverName}`;
  if (destinationPlace === 'nudos') return `Bodegas ${destinationCountryName}`;
  return '';
};

/**
 * Reemplaza todos los placeholders de EVENTS_TYPES_CONTENT en un texto usando relevantInfo
 * @param text - Texto que puede contener placeholders [usuario] y [destino]
 * @param relevantInfo - Información relevante que contiene userId, userName y destinationName
 * @returns Texto con todos los placeholders reemplazados y capitalizado
 */
export const replaceEventPlaceholders = (
  text: string,
  userId?: number | null,
  relevantInfo?: IRelevantInfo
): string => {
  // Definir el placeholder de usuario
  let userReplacement = 'Usuario no identificado';
  if (userId !== null && userId !== undefined && [0, 1].includes(userId)) {
    userReplacement = 'Sistema';
  }
  if (relevantInfo?.responsible) {
    userReplacement = relevantInfo.responsible.fullNameOrEmail;
  }
  // Definir el placeholder de destino
  const destinationReplacement = relevantInfo?.destination
    ? replaceDestinationPlaceholder(relevantInfo.destination)
    : 'Destino no identificado';

  // Aplicar los reemplazos
  let replacedText = text.replace(REPLACE_KEYS.USER, userReplacement);
  replacedText = replacedText.replace(REPLACE_KEYS.DESTINATION, destinationReplacement);

  // Capitalizar la primera letra del texto
  const capitalized = replacedText.charAt(0).toUpperCase() + replacedText.slice(1);
  return capitalized;
};

interface IEventContent {
  name: string | null;
  description: string | null;
}

/**
 * Mapper para obtener el nombre y la descripción de un evento basado en su UUID
 * @param eventUuid - UUID del evento
 * @returns Objeto con name y description, o valores por defecto si no se encuentra
 */
export const getEventContentByUuid = (eventUuid: string): IEventContent => {
  const initialValue = { name: null, description: null };
  if (!eventUuid) return initialValue;
  const event = EVENTS_TYPES_CONTENT[eventUuid as keyof typeof EVENTS_TYPES_CONTENT];
  if (!event) return initialValue;
  return { name: event.title, description: event.subtitle };
};

/**
 * Modifica el contenido de un evento basado en su UUID
 * @param event - Evento a modificar
 * @returns Objeto con name y description, o valores por defecto si no se encuentra
 */
export const modifyEventContent = (event: ILogisticServiceHistoryEvent | IOrderHistoryEvent): IEventContent => {
  const eventContent = getEventContentByUuid(event.eventType?.uuid || '');
  const name = eventContent.name || event.eventType?.name || '';
  const description = replaceEventPlaceholders(eventContent.description || '', event.userId, event.relevantInfo);
  return { name, description };
};
